import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/Layout";

const LAST_UPDATED = "9 June 2026";

export default function Privacy() {
  return (
    <Layout>
      <Helmet>
        <title>Privacy Policy | IndiaBusinessCircle</title>
        <meta name="description" content="How IndiaBusinessCircle collects, uses, and protects your business and personal data, including GST information, on India's verified B2B network." />
        <link rel="canonical" href={`${window.location.origin}/privacy`} />
      </Helmet>

      <section className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Legal</span>
          <h1 data-testid="privacy-title" className="mt-3 font-display font-black tracking-tight text-3xl sm:text-4xl text-slate-900">Privacy Policy</h1>
          <p className="mt-3 text-slate-500 text-sm">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-5 sm:px-8 py-10 prose-legal">
        <p className="text-slate-600 leading-relaxed">
          IndiaBusinessCircle ("IBC", "we", "us", or "our") operates a verified business networking
          platform for Indian businesses. This Privacy Policy explains what information we collect,
          how we use it, and the choices you have. By using IBC, you agree to the practices described here.
        </p>

        <Section title="1. Information We Collect">
          <ul>
            <li><strong>Account data:</strong> your name, email address, and (for email sign-up) a securely hashed password. If you sign in with Google, we receive your name, email, and profile picture.</li>
            <li><strong>Business profile data:</strong> business name, trade name, GST Identification Number (GSTIN), category, city/state, phone number, description, products/services, and any logo, cover, photos, or videos you upload.</li>
            <li><strong>Networking activity:</strong> follows, connection requests, messages between connected businesses, posts, comments, and reports.</li>
            <li><strong>Usage & technical data:</strong> log data, device/browser information, and cookies used to keep you signed in and to secure the platform.</li>
          </ul>
        </Section>

        <Section title="2. How We Handle GST Information">
          <p>
            We use your GSTIN to display a "GST Verified" badge based on a format validity check. Your full
            GSTIN is <strong>masked on public pages</strong> (e.g., 27XXXXXXXX1Z5) and is revealed only to you (the
            owner), platform administrators, and businesses you have accepted as connections. We do not sell your
            GSTIN, and we do not use it for any purpose other than verification and trust signalling on the platform.
          </p>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul>
            <li>To create and operate your account and business profile.</li>
            <li>To power discovery, search, personalised recommendations, and networking features.</li>
            <li>To display verification badges and enable trusted B2B connections.</li>
            <li>To send transactional notifications (new followers, connection requests, messages, comments).</li>
            <li>To maintain security, prevent abuse, and enforce our Terms of Service.</li>
            <li>To process subscription payments through our payment partner.</li>
          </ul>
        </Section>

        <Section title="4. Public vs. Private Information">
          <p>
            Your business name, category, location, description, products, logo, and gallery are public by design so
            that other businesses can discover you. Your phone number and full GSTIN are private and shown only to
            authorised parties as described in Section 2. Suspended profiles are removed from public listings.
          </p>
        </Section>

        <Section title="5. Cookies & Sessions">
          <p>
            We use an httpOnly session cookie to keep you securely signed in. We may use privacy-respecting analytics
            to understand and improve the product. You can control cookies through your browser settings, though some
            features may not work without them.
          </p>
        </Section>

        <Section title="6. Sharing & Disclosure">
          <p>We do not sell your personal data. We share information only:</p>
          <ul>
            <li>With other businesses, per the public/private rules above.</li>
            <li>With service providers (e.g., hosting, object storage, payment processing) strictly to operate IBC.</li>
            <li>Where required by Indian law, regulation, legal process, or to protect rights and safety.</li>
          </ul>
        </Section>

        <Section title="7. Data Security & Retention">
          <p>
            We apply reasonable technical and organisational safeguards, including password hashing, access controls,
            and login-attempt protection. We retain your data for as long as your account is active or as needed to
            provide the service and meet legal obligations. No method of transmission or storage is 100% secure.
          </p>
        </Section>

        <Section title="8. Your Rights & Choices">
          <ul>
            <li>Access, update, or correct your profile at any time from your dashboard.</li>
            <li>Request deletion of your account and associated data by contacting us.</li>
            <li>Manage notifications and connection preferences within the app.</li>
          </ul>
        </Section>

        <Section title="9. Children">
          <p>IBC is a business platform intended for users aged 18 and above. We do not knowingly collect data from children.</p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. Material changes will be reflected by the "Last updated"
            date above. Continued use of IBC after changes constitutes acceptance of the revised policy.
          </p>
        </Section>

        <Section title="11. Contact Us">
          <p>
            For any privacy questions or requests, contact us at{" "}
            <a href="mailto:support@indiabusinesscircle.com" className="text-brand font-semibold">support@indiabusinesscircle.com</a>.
          </p>
        </Section>
      </article>
    </Layout>
  );
}

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-display font-bold text-xl text-slate-900 mb-3">{title}</h2>
    <div className="text-slate-600 leading-relaxed space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_a]:underline">
      {children}
    </div>
  </section>
);
